// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import clsx from "clsx";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import PortfoliosService from "../../portfolios.service";

const AllPortfolios: React.FC = (): ReactElement => {
  let searchTimer = null;
  const portfoliosService = new PortfoliosService();
  const loadingState = useSelector((state: any) => state?.loading);
  const navigate = useNavigate();
  const [allRequestsList, setAllRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchCompletedListData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.accountId}/${record?.account?.activeKycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_, record) => record?.account?.activeKyc?.nameEn,
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Portfolio Type",
      dataIndex: "portfolioType",
      key: "portfolioType",
      render: (_, record) => <span>{record?.portfolioType?.nameEn}</span>,
    },
    {
      title: "Portfolio#",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
    },
    {
      title: "Request status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      filters: [
        {
          text: "Approved",
          value: "Approved",
        },
        {
          text: "Opened",
          value: "Opened",
        },
        {
          text: "Pending",
          value: "Pending",
        },
        {
          text: "Rejected",
          value: "Rejected",
        },
      ],
      // onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={clsx(
            "rounded bg-gray-600 text-xs font-medium w-fit py-2 px-3 text-nowrap",
            text !== "Rejected"
              ? "bg-gray-600 text-green-500"
              : "bg-pink-200 text-pink-300"
          )}
        >
          {text === "PENDING_DEPOSIT" ? "Pending Deposit" : text}
        </span>
      ),
    },
    {
      title: "Origin",
      dataIndex: "origin",
      key: "origin",
      render: (text) =>
        text !== "APP" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Action by",
      dataIndex: "actionBy",
      key: "actionBy",
      render: (_, record) => <span>{record?.actionBy?.name || "-"}</span>,
    },
    {
      title: "Action Date",
      dataIndex: "actionDate",
      key: "actionDate",
      sorter: true,
      render: (text) =>
        text ? (
          <span className=" text-gray-500">
            {moment(text).format("MMM DD YYYY hh:mm A")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <div className="flex items-center">
          <ReactSVG
            src={"images/view.svg"}
            onClick={() => {
              navigate(`/portfolio/take-action/${record.id}`);
            }}
          />
        </div>
      ),
    },
  ];

  const fetchCompletedListData = async (page = 1): Promise<void> => {
    const data = await portfoliosService.getPortfoliosListData(
      ["Pending", "PENDING_DEPOSIT", "Opened", "Rejected", "Blocked"],
      true,
      page,
      searchWord,
      sortField,
      sortOrder,
      filters
    );

    if (data !== undefined) {
      setResponseDate(data.data);
      setAllRequestsList(data.data?.portfolios);
    }
  };

  useEffect(() => {
    fetchCompletedListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? allRequestsList?.length : ""} requests
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {allRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={allRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchCompletedListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default AllPortfolios;
